import { Injectable, EventEmitter } from '@angular/core';
import { User } from '../models/user';
import { LoginService } from './login.service';
import { Observable,  of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConnectionService } from '../../../services/connection/connection.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { Router } from '@angular/router';
import { LocalizationService } from 'src/app/services/localization.service';

@Injectable()
export class LoginStateService {

  private _user: User;

  eventUserLoggedIn = new EventEmitter<User>();
  eventUserLoggedOut = new EventEmitter<User>();

  constructor(
    private loginService: LoginService,
    private connectionService: ConnectionService,
    private logger: LoggerService,
    private router: Router,
    private localizationService: LocalizationService,
  ) {
    this.connectionService.authorizationTokenExpired.subscribe(
      result => {
        this._doLogout();
        this.router.navigateByUrl('/login');
      }
    );

    this.connectionService.authorizationTokenUpdated
    .pipe(
      switchMap((key) => {
        if(key) {
          return this.initialize();
        }
        return of(false);
      }),
    )
    .subscribe();
  }

  initialize(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        this.logger.info(`LoginStateService: initialize.`);

        if (this._user) {
          return of(true);
        }

        this.logger.info(`LoginStateService: initialize. start to load logged-in user info.`);

        return this.loginService.loggedInUser.pipe(
          map(user => {
            const logoutUser = this._user;
            const sendLogginEvent = !this._user;

            this._user = user;
            if (!user) {
              this.logger.info(`LoginStateService: initialize. logged-in user was no found.`);
              this.connectionService.clearAuthorizationToken();
              if (logoutUser) {
                this.eventUserLoggedOut.emit(logoutUser);
              }
            } else {
              this.logger.info(`LoginStateService: initialize. logged-in user ${user.login} found.`);
              if (sendLogginEvent) {
                this.eventUserLoggedIn.emit(this._user);
              }
            }

            return true;
          }),
          catchError(error => {
            this.logger.info(`LoginStateService: initialize. Error: ${error}.`);
            this.connectionService.clearAuthorizationToken();
            this.eventUserLoggedOut.emit(this._user);
            return of(false);
          })
        );
      }),
    );
  }

  login(
    login: string,
    password: string,
    rememberMe: boolean,
    database?: string
  ): Observable<User> {
    login = login ? login.toLowerCase() : login;
    return this.loginService.login(
      login,
      password,
      database).pipe(map((user: User) => {
        this._user = user;
        if (user && user.session_id) {
          this.logger.info(`LoginStateService: login ${user.id} ${user.login}`);
          this.connectionService.setAuthorizationToken(user.session_id, !rememberMe);
          this.localizationService.initialize();
          this.eventUserLoggedIn.emit(user);
        } else {
          this.logger.info(`LoginStateService: login failed user is undefined.`);
          this.connectionService.clearAuthorizationToken();
        }
        return user;
      }));
  }

  private _doLogout(): void {
    this.logger.info(`LoginStateService: _doLogout.`);
    const user = this._user;
    this._user = null;
    this.connectionService.clearAuthorizationToken();
    if (user) {
      this.eventUserLoggedOut.emit(user);
    }
  }

  logout(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (this._user === null) {
          this.logger.info(`LoginStateService: logout user is already undefined.`);
          return of(null);
        }

        return this.loginService.logout().pipe(
          map(done => {
            this.logger.info(`LoginStateService: logout done.`);
            this._doLogout();
            return true;
          }),
          catchError(error => {
            if (error && (error.status === 303 || error.status === 200)) {
              this.logger.info(`LoginStateService: logout done. Received error 303 from odoo server.`);
              this._doLogout();
              return of(true);
            } else {
              this.logger.info(`LoginStateService: logout failed error ${error}.`);
              return throwError(error);
            }
          }),
        );
      })
    );
  }

  get isLoggedIn(): boolean {
    return !!this.loggedInUser;
  }

  get loggedInUser(): User {
    return this._user;
  }

  resetPassword(email: string): Observable<string> {
    return this.loginService.resetPassword(email);
  }

  resetPasswordByToken(token: string, password: string, confirmPassword: string): Observable<string> {
    return this.loginService.resetPasswordByToken(token, password, confirmPassword);
  }

  changePassword(password: string, newPassword): Observable<boolean> {
    return this.loginService.changePassword(password, newPassword);
  }
}
